import { create } from 'zustand';
import dayjs, { Dayjs } from 'dayjs';
import { DatePreset } from 'app/components/datepicker-dropdown/enums';
import {
  getDatesFromPreset,
  getDatesWithConstraints,
} from 'app/components/datepicker-dropdown/utils';
import {
  ComparisonPreset,
  isComparisonPreset,
  resolveComparisonRange,
} from 'app/modules/analytics/utils/comparisonPresets';
import { getPrecedingComparisonRange } from 'app/modules/analytics/utils/getPrecedingComparisonRange';
import { AnalyticsFiltersSnapshot } from 'app/modules/analytics/utils/analyticsFiltersSerialization';

export interface AnalyticsFiltersState {
  startDate: Dayjs;
  endDate: Dayjs;
  datePreset: string | null;
  isComparisonEnabled: boolean;
  comparisonStartDate: Dayjs;
  comparisonEndDate: Dayjs;
  comparisonDatePreset: string | null;
  setDateRange: (
    dates: { startDate: Dayjs | null; endDate: Dayjs | null },
    preset?: string,
  ) => void;
  setComparisonEnabled: (enabled: boolean) => void;
  setComparisonDateRange: (
    dates: { startDate: Dayjs | null; endDate: Dayjs | null },
    preset?: string,
  ) => void;
  /** One-time application of resolved view state (URL, then persisted preset). */
  applyResolvedFilters: (filters: AnalyticsFiltersSnapshot) => void;
  reset: () => void;
}

function defaultRange() {
  const { startDate, endDate } = getDatesWithConstraints(
    getDatesFromPreset(DatePreset.THIS_MONTH),
    { maxEndDate: dayjs().endOf('day') },
  );
  return { startDate: startDate!, endDate: endDate! };
}

function initialState() {
  const { startDate, endDate } = defaultRange();
  const comparison = getPrecedingComparisonRange(startDate, endDate);

  return {
    startDate,
    endDate,
    datePreset: DatePreset.THIS_MONTH as string | null,
    isComparisonEnabled: false,
    comparisonStartDate: comparison.comparisonStartDate,
    comparisonEndDate: comparison.comparisonEndDate,
    comparisonDatePreset: ComparisonPreset.PREVIOUS_PERIOD as string | null,
  };
}

export const useAnalyticsFiltersStore = create<AnalyticsFiltersState>()(
  (set, get) => ({
    ...initialState(),

    // One atomic transition: the comparison range follows the main range in
    // the same update, rather than an effect reacting to it afterwards.
    setDateRange: (dates, preset) => {
      const next: Partial<AnalyticsFiltersState> = {
        datePreset: preset !== undefined ? preset : null,
      };

      if (dates.startDate && dates.endDate) {
        next.startDate = dates.startDate;
        next.endDate = dates.endDate;

        const current = get();
        if (current.isComparisonEnabled) {
          const activePreset = isComparisonPreset(current.comparisonDatePreset)
            ? current.comparisonDatePreset
            : ComparisonPreset.PREVIOUS_PERIOD;
          const comparison = resolveComparisonRange(
            activePreset,
            dates.startDate,
            dates.endDate,
          );

          if (comparison) {
            next.comparisonStartDate = comparison.startDate;
            next.comparisonEndDate = comparison.endDate;
            next.comparisonDatePreset = activePreset;
          }
        }
      }

      set(next);
    },

    setComparisonEnabled: (enabled) => {
      if (!enabled) {
        set({ isComparisonEnabled: false });
        return;
      }

      const comparison = resolveComparisonRange(
        ComparisonPreset.PREVIOUS_PERIOD,
        get().startDate,
        get().endDate,
      );

      set({
        isComparisonEnabled: true,
        ...(comparison && {
          comparisonStartDate: comparison.startDate,
          comparisonEndDate: comparison.endDate,
          comparisonDatePreset: ComparisonPreset.PREVIOUS_PERIOD,
        }),
      });
    },

    setComparisonDateRange: (dates, preset) => {
      set({
        comparisonDatePreset: preset !== undefined ? preset : null,
        ...(dates.startDate &&
          dates.endDate && {
            comparisonStartDate: dates.startDate,
            comparisonEndDate: dates.endDate,
          }),
      });
    },

    applyResolvedFilters: (filters) => set({ ...filters }),

    // The store outlives the route; the provider resets on mount so entering
    // analytics is a clean slate. Only the URL carries a range across a reload.
    reset: () => set(initialState()),
  }),
);
